import type { ScreenshotOptions } from './screenshot'

const SCREENSHOT_WORKER_FILE = '/workers/ScreenshotEncoderWorker.js'

type ScreenshotWorkerResult =
  | { type: 'ready' }
  | { type: 'result'; blob: Blob }
  | { type: 'error'; message: string }

function resolveWorkerUrl(workerBaseUrl?: string): string {
  const configuredBaseUrl =
    workerBaseUrl ?? (typeof window !== 'undefined' ? window.POTREE_BASE_URL : undefined)
  if (configuredBaseUrl) {
    return `${configuredBaseUrl.replace(/\/+$/, '')}${SCREENSHOT_WORKER_FILE}`
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${SCREENSHOT_WORKER_FILE}`
  }
  return SCREENSHOT_WORKER_FILE
}

export function supportsScreenshotWorkerEncoding(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof globalThis.createImageBitmap === 'function'
  )
}

/** 在 Worker 中编码合成后的截图画布。 */
export async function encodeCanvasInWorker(
  canvas: HTMLCanvasElement,
  options: Pick<ScreenshotOptions, 'mimeType' | 'quality' | 'workerBaseUrl'>
): Promise<Blob> {
  if (!supportsScreenshotWorkerEncoding()) {
    throw new Error('当前环境不支持截图 Worker 编码')
  }

  const bitmap = await globalThis.createImageBitmap(canvas)
  let worker: Worker
  try {
    worker = new Worker(resolveWorkerUrl(options.workerBaseUrl), { type: 'module' })
  } catch (error) {
    bitmap.close()
    throw error instanceof Error ? error : new Error(String(error))
  }

  return new Promise<Blob>((resolve, reject) => {
    let settled = false
    const cleanup = () => {
      worker.removeEventListener('message', onMessage)
      worker.removeEventListener('error', onError)
      worker.terminate()
    }
    const settleError = (error: Error) => {
      if (settled) {
        return
      }
      settled = true
      cleanup()
      reject(error)
    }
    const onMessage = (event: MessageEvent<ScreenshotWorkerResult>) => {
      const data = event.data
      if (!data || data.type === 'ready') {
        return
      }
      if (data.type === 'result') {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        resolve(data.blob)
        return
      }
      settleError(new Error(data.message || '截图 Worker 编码失败'))
    }
    const onError = (event: ErrorEvent) => {
      settleError(new Error(event.message || '截图 Worker 启动失败'))
    }

    worker.addEventListener('message', onMessage)
    worker.addEventListener('error', onError)
    try {
      worker.postMessage(
        {
          type: 'encode',
          bitmap,
          width: canvas.width,
          height: canvas.height,
          mimeType: options.mimeType ?? 'image/jpeg',
          quality: options.quality,
        },
        [bitmap]
      )
    } catch (error) {
      bitmap.close()
      settleError(error instanceof Error ? error : new Error(String(error)))
    }
  })
}
